import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, Gift, Loader2, X } from 'lucide-react';
import { DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { ExamDialog } from 'components/exam/ExamDialog';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { formatGroupedNumber } from 'lib/dateFormat';
import { cn } from 'lib/utils';
import { createPortalCheckout, getPortalPaymentMethods } from 'modules/portal-plans/api';
import type { PlanOffer, PortalPlan } from 'modules/portal-plans/offer';
import { isOnlineMethod, pickPaymentMethod, type OnlinePaymentMethod, type PaymentMethod } from 'modules/payments/methods';
import { PaymentMethodMark } from 'components/payments/PaymentMethodMark';

const METHOD_NAMES: Record<OnlinePaymentMethod, string> = {
  payme: 'Payme', click: 'Click', uzum: 'Uzum', uzcard: 'Uzcard', humo: 'Humo', visa_mc: 'Visa / Mastercard',
};
type PlanId = 'START' | 'PRO' | 'PREMIUM';

const KNOWN_PLANS: readonly string[] = ['START', 'PRO', 'PREMIUM'];

/** Plan names are seeded as keys ("PRO"); the dialog reads them as words ("Pro"). */
const planLabel = (name: string): string =>
  name === name.toUpperCase() ? name.charAt(0) + name.slice(1).toLowerCase() : name;

/** What the plan unlocks, in the learner's language, from the flags the catalogue already carries. */
function unlockedFeatures(plan: PortalPlan): Array<{ key: string; count?: number }> {
  const list: Array<{ key: string; count?: number }> = [];
  if (plan.mockLimit) list.push({ key: 'feature_mocks', count: plan.mockLimit });
  if (plan.analysisLimit) list.push({ key: 'feature_exam_insights', count: plan.analysisLimit });
  if (plan.hasAnswerAnalysis) list.push({ key: 'feature_analysis' });
  if (plan.hasStudyPlan) list.push({ key: 'feature_study_plan' });
  if (plan.hasTeacherConsultation) list.push({ key: 'feature_teacher' });
  if (plan.hasPersonalAnalytics) list.push({ key: 'feature_analytics' });
  return list;
}

export interface SessionEndOfferDialogProps {
  open: boolean;
  offer: PlanOffer;
  /** Called on "Maybe later", the close button, Escape and the scrim, and after the card sheet closes. */
  onClose: () => void;
}

/**
 * The "special offer" shown once after a test. Every number on it is the one the checkout charges: the
 * struck price is the plan's list price, the big one its permanent discounted price. There is no countdown,
 * because nothing here expires.
 */
export function SessionEndOfferDialog({ open, offer, onClose }: SessionEndOfferDialogProps) {
  const { t } = useTranslation();
  const { plan, saving, percentOff } = offer;
  const cardAvailable = KNOWN_PLANS.includes(plan.key);

  const [chosenMethod, setMethod] = useState<PaymentMethod | null>(null);
  const offered = useQuery({
    queryKey: ['portal-payment-methods'],
    queryFn: ({ signal }) => getPortalPaymentMethods(signal),
    enabled: open,
    staleTime: 60_000,
  });
  // The card transfer opens the full checkout, which only knows the seeded plans.
  const methods = (offered.data ?? []).filter((id) => cardAvailable || id !== 'card');
  const method = pickPaymentMethod(methods, chosenMethod);
  const [cardOpen, setCardOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const request = useRef<AbortController | null>(null);
  const payRef = useRef<HTMLButtonElement>(null);

  useEffect(() => () => request.current?.abort(), []);

  const som = (amount: number) => t('session_end.amount', { value: formatGroupedNumber(amount) });
  const features = unlockedFeatures(plan);
  const name = planLabel(plan.name);

  const payOnline = async (online: OnlinePaymentMethod) => {
    if (request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setPaying(true);
    setFailed(false);
    try {
      const checkout = await createPortalCheckout(plan.key, plan.discountPrice, online, controller.signal);
      if (!controller.signal.aborted) window.location.assign(checkout.checkoutUrl);
    } catch {
      if (!controller.signal.aborted) setFailed(true);
    } finally {
      if (request.current === controller) request.current = null;
      setPaying(false);
    }
  };

  const pay = () => {
    if (!method) return;
    if (isOnlineMethod(method)) void payOnline(method);
    else setCardOpen(true);
  };

  return (
    <>
      <ExamDialog open={open && !cardOpen} onClose={onClose} widthClass="max-w-[860px] p-0" initialFocusRef={payRef}>
        <div className="grid md:grid-cols-[1fr_1.05fr]">
          <aside className="rounded-t-lg bg-[#0B1B4D] p-6 text-white md:rounded-l-lg md:rounded-tr-none md:p-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-foreground">
              <Gift className="h-3.5 w-3.5" aria-hidden />
              {t('session_end.offer_badge')}
            </span>
            <DialogTitle className="mt-4 text-[22px] md:mt-5 md:text-[26px] font-bold leading-tight tracking-[-0.02em]">
              {t('session_end.offer_title')}
            </DialogTitle>
            <DialogDescription className="mt-2 hidden text-sm leading-relaxed text-white/75 md:block">
              {t('session_end.offer_lead', { plan: name })}
            </DialogDescription>

            {features.length > 0 ? (
              <div className="mt-6 hidden md:block">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/60">
                  {t('session_end.offer_unlocks')}
                </p>
                <ul className="mt-3 space-y-2.5">
                  {features.map((feature) => (
                    <li key={feature.key} className="flex items-start gap-3 text-sm">
                      <span
                        aria-hidden
                        className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      {t(`session_end.${feature.key}`, { count: feature.count })}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </aside>

          <section className="relative p-6 md:p-8">
            <button
              type="button"
              onClick={onClose}
              aria-label={t('session_end.offer_close')}
              className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>

            <div className="flex items-center gap-3 pr-10">
              <span className="rounded-md bg-foreground px-2.5 py-1 text-sm font-bold text-background">
                {t('session_end.offer_percent', { percent: percentOff })}
              </span>
              <span className="text-sm font-medium">{name}</span>
            </div>

            <div className="mt-5">
              <p className="text-sm text-muted-foreground">
                <span className="sr-only">{t('session_end.offer_list_price')}: </span>
                <s>{som(plan.monthlyPrice)}</s>
              </p>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
                <span className="sr-only">{t('session_end.offer_your_price')}: </span>
                <span className="text-[34px] font-bold leading-none tracking-[-0.03em]">{som(plan.discountPrice)}</span>
                <span className="text-sm text-muted-foreground">{t('session_end.offer_per_month')}</span>
              </p>
              <p className="mt-3 inline-flex rounded-md bg-primary/25 px-2.5 py-1 text-sm font-semibold">
                {t('session_end.offer_saving', { amount: som(saving) })}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">{t('session_end.offer_included')}</p>
            </div>

            <div className="mt-6">
              <p id="session-end-method" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('session_end.offer_method')}
              </p>
              {offered.isLoading ? (
                <Loader2 className="mt-2 h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
              ) : methods.length === 0 ? (
                <p role="alert" className="mt-2 text-sm text-muted-foreground">
                  {t('session_end.offer_no_methods')}
                </p>
              ) : null}
              <div role="radiogroup" aria-labelledby="session-end-method" className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {methods.map((id) => {
                  const checked = method === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={checked}
                      tabIndex={checked ? 0 : -1}
                      onClick={() => setMethod(id)}
                      onKeyDown={(event) => {
                        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
                        event.preventDefault();
                        const index = methods.indexOf(id);
                        const step = event.key === 'ArrowRight' ? 1 : -1;
                        setMethod(methods[(index + step + methods.length) % methods.length]);
                      }}
                      className={cn(
                        'flex h-11 items-center justify-center rounded-lg border px-3 text-sm font-medium transition-[border-color,box-shadow] duration-150',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                        checked ? 'border-foreground ring-1 ring-foreground' : 'border-border hover:border-foreground/30',
                      )}
                    >
                      <PaymentMethodMark method={id} cardLabel={t('session_end.offer_method_card')} />
                    </button>
                  );
                })}
              </div>
            </div>

            {failed ? (
              <p role="alert" className="mt-4 text-sm text-destructive">
                {t('session_end.offer_error')}
              </p>
            ) : null}

            <Button ref={payRef} size="lg" className="mt-5 h-12 w-full text-base font-bold" onClick={pay} disabled={paying || !method}>
              {paying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : null}
              {method && isOnlineMethod(method)
                ? t('session_end.offer_pay_online', { method: METHOD_NAMES[method], amount: som(plan.discountPrice) })
                : t('session_end.offer_pay_card', { amount: som(plan.discountPrice) })}
            </Button>
            <Button variant="ghost" className="mt-2 w-full" onClick={onClose}>
              {t('session_end.offer_later')}
            </Button>
          </section>
        </div>
      </ExamDialog>

      {cardAvailable ? (
        <PlanCheckoutModal
          open={open && cardOpen}
          defaultPlan={plan.key as PlanId}
          defaultPaymentMethod="card"
          onOpenChange={(next) => {
            if (next) return;
            setCardOpen(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
